import { useCallback, useEffect, useRef, useState } from "react";
import { useProjects } from "@contexts/ProjectContext";
import type { Project } from "@shared/types";
import type { ProjectDocRepository } from "../firestore/ProjectDocRepository";

// ProjectContext.jsx is still JS — cast until it's migrated to TSX.
interface ProjectContextValue {
  activeProject: Project | null | undefined;
}

/**
 * Subscribes to a ProjectDocRepository scoped to the active project, and
 * returns a React-friendly [value, setValue, loaded] tuple — compatible with
 * the old useFirestoreDoc API shape so refactors are drop-in.
 *
 * Writes are debounced (400ms) to avoid hammering Firestore during rapid
 * keystroke-driven edits.
 */
export function useProjectDoc<T>(
  repository: ProjectDocRepository<T>
): [T, (next: T | ((prev: T) => T)) => void, boolean] {
  const projects = useProjects() as ProjectContextValue;
  const activeProject = projects.activeProject;

  const [value, setValue] = useState<T>(repository.defaultValue);
  const [loaded, setLoaded] = useState(false);
  const writeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!activeProject) {
      setValue(repository.defaultValue);
      setLoaded(false);
      return undefined;
    }
    const unsubscribe = repository.watch(
      activeProject,
      (next, isLoaded) => {
        setValue(next);
        setLoaded(isLoaded);
      }
    );
    return unsubscribe;
  }, [repository, activeProject]);

  const update = useCallback(
    (next: T | ((prev: T) => T)) => {
      setValue((prev) => {
        const resolved =
          typeof next === "function"
            ? (next as (prev: T) => T)(prev)
            : next;
        if (activeProject) {
          if (writeTimer.current) clearTimeout(writeTimer.current);
          writeTimer.current = setTimeout(() => {
            repository.set(activeProject, resolved).catch((err) => {
              console.error(`[useProjectDoc:${repository.key}] write failed`, err);
            });
          }, 400);
        }
        return resolved;
      });
    },
    [repository, activeProject]
  );

  return [value, update, loaded];
}
